import React, { useState } from "react";

// Generate 30-minute interval time slots from 4:00 AM to 10:00 PM
const generateTimeSlots = () => {
  const slots = [];
  const startHour = 4;
  const endHour = 22;
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      slots.push(
        `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
      );
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

const initialEvent = {
  id: 1,
  name: "Photo permis",
  day: 0,
  startTime: "08:00",
  endTime: "09:30",
};

const Test = () => {
  const [events, setEvents] = useState([initialEvent]);
  const [resizingEventId, setResizingEventId] = useState(null);
  const [moving, setMoving] = useState(false);
  const [hoveredSlot, setHoveredSlot] = useState(null);

  const handleDragStart = (e, event) => {
    setMoving(true);
    e.dataTransfer.setData("eventId", event.id);
  };

  const handleDrop = (e, newStartTime, day) => {
    setMoving(false);
    setHoveredSlot(null);
    const eventId = e.dataTransfer.getData("eventId");
    if (!eventId) return;

    setEvents((prevEvents) =>
      prevEvents.map((event) =>
        event.id === parseInt(eventId)
          ? {
              ...event,
              startTime: newStartTime,
              endTime: calculateEndTime(
                newStartTime,
                event.startTime,
                event.endTime
              ),
              day,
            }
          : event
      )
    );
  };

  const handleDragOver = (e, time) => {
    e.preventDefault();
    setHoveredSlot(time);
  };

  const handleResizeStart = (e, eventId) => {
    e.preventDefault();
    setResizingEventId(eventId);
  };

  const handleMouseMove = (e) => {
    if (!resizingEventId) return;

    const clientY = e.clientY;
    const calendarTop = document
      .querySelector(".timeline")
      .getBoundingClientRect().top;

    // Calculate the new duration based on mouse position
    const event = events.find((ev) => ev.id === resizingEventId);
    if (event) {
      const newEndTimeIndex = Math.floor((clientY - calendarTop) / 24); // 24px height per 30-minute interval
      if (newEndTimeIndex > timeSlots.indexOf(event.startTime)) {
        const newEndTime = timeSlots[newEndTimeIndex];
        setEvents((prevEvents) =>
          prevEvents.map((ev) =>
            ev.id === resizingEventId
              ? {
                  ...ev,
                  endTime: newEndTime,
                }
              : ev
          )
        );
      }
    }
  };

  const handleMouseUp = () => {
    setResizingEventId(null);
  };

  return (
    <div
      className="flex w-full overflow-y-auto bg-gray-800 "
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div className="flex flex-row w-full">
        {[0, 1, 2, 3, 4].map((day, index) => (
          <div className="flex flex-col w-full font-sans">
            <div className="flex items-center justify-between px-5 py-2 bg-gray-900 text-white">
              <span>LUN.</span>
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-lg">
                11
              </div>
            </div>
            <div className="relative flex flex-col   text-white timeline">
              {timeSlots.map((time, index) => (
                <div
                  key={time}
                  onDrop={(e) => handleDrop(e, time, day)}
                  onDragOver={(e) => handleDragOver(e, time)}
                  className={`relative border-b border-gray-600 px-3 h-6 ${
                    hoveredSlot === time ? "bg-gray-700" : ""
                  }`}
                >
                  <span className="text-xs text-gray-400">{time}</span>
                </div>
              ))}
              {events.map(
                (event) =>
                  event.day === day && (
                    <div
                      key={event.id}
                      className="absolute left-16 w-[200px] p-1  text-h3 rounded-md text-white bg-purple-500 "
                      style={{
                        top: `${timeSlots.indexOf(event.startTime) * 24 + 0}px `, // 48px height per 30-min interval
                        height: moving
                          ? "20px"
                          : calculateEventHeight(
                              event.startTime,
                              event.endTime
                            ),
                        backgroundColor: moving ? "transparent" : "#a855f7",
                      }}
                    >
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, event)}
                        className="absolute top-0 left-0 right-0  cursor-grab bg-purple-700"
                        style={{
                          height: moving ? "30px" : "6px",
                          borderRadius: moving ? "10px" : "0",
                        }}
                      ></div>
                      <strong>{event.name}</strong>
                      <p className="text-xs">{`${event.startTime} - ${event.endTime}`}</p>
                      {!moving && (
                        <div
                          onMouseDown={(e) => handleResizeStart(e, event.id)}
                          className="absolute bottom-0 left-0 right-0 h-1 cursor-s-resize bg-purple-700"
                        ></div>
                      )}
                    </div>
                  )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to calculate event end time based on duration
const calculateEndTime = (newStartTime, originalStartTime, originalEndTime) => {
  const [startHour, startMinute] = newStartTime.split(":").map(Number);
  const [origStartHour, origStartMinute] = originalStartTime
    .split(":")
    .map(Number);
  const [origEndHour, origEndMinute] = originalEndTime.split(":").map(Number);

  const durationMinutes =
    origEndHour * 60 + origEndMinute - (origStartHour * 60 + origStartMinute);
  const endTotalMinutes = startHour * 60 + startMinute + durationMinutes;
  const endHour = Math.floor(endTotalMinutes / 60);
  const endMinute = endTotalMinutes % 60;

  return `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`;
};

// Helper function to calculate the height of the event based on its duration
const calculateEventHeight = (startTime, endTime) => {
  const [startHour, startMinute] = startTime.split(":").map(Number);
  const [endHour, endMinute] = endTime.split(":").map(Number);

  const durationInMinutes =
    endHour * 60 + endMinute - (startHour * 60 + startMinute);
  const heightPer30Min = 24; // Assuming each 30-min slot is 24px high

  return `${(durationInMinutes / 30) * heightPer30Min}px`;
};

export default Test;
